import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

/**
 * Minimal shape required by the version rail — only the fields it actually reads.
 */
export interface VersionRailItem {
  /**
   * Unique identifier of this version, used for selection and navigation.
   */
  readonly id: number;

  /**
   * Date shown for the version.
   */
  readonly versionDisplayDate: Date;

  /**
   * Display name of the user who created or amended this version, or null when unavailable.
   */
  readonly versionActorName: string | null;

  /**
   * Whether this version was originally created or produced by an amendment.
   */
  readonly versionActorAction: 'created' | 'amended';
}

@Component({
  selector: 'fb-version-rail',
  imports: [DatePipe],
  templateUrl: './version-rail.component.html',
  styleUrl: './version-rail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VersionRailComponent {
  /**
   * Ordered version history, oldest first.
   */
  public readonly versions = input.required<readonly VersionRailItem[]>();

  /**
   * ID of the version currently on screen, or null when none is selected yet.
   */
  public readonly selection = input<number | null>(null);

  /**
   * Emits the ID of the version the user navigated to.
   */
  public readonly selectionChange = output<number>();

  /**
   * Version history in correct order.
   */
  protected readonly reversedVersions = computed(() => this.versions().slice().reverse());

  /**
   * Checks whether the version is selected.
   */
  protected isSelected(item: VersionRailItem): boolean {
    return this.selection() === item.id;
  }

  /**
   * Marks version as selected.
   */
  protected selectVersion(item: VersionRailItem): void {
    this.selectionChange.emit(item.id);
  }

  /**
   * Builds a title displayed for the version.
   */
  protected getTitle(item: VersionRailItem): string {
    const index = this.versions().findIndex((target) => target.id === item.id);
    const last = index + 1 === this.versions().length;
    const first = index === 0;

    if (first) {
      return 'Original';
    }

    if (last) {
      return `Amendment ${index} (Current)`;
    }

    return `Amendment ${index}`;
  }

  /**
   * Label describing the actor who created/amended this version, or null.
   */
  protected getActorLabel(item: VersionRailItem): string | null {
    if (!item.versionActorName) {
      return null;
    }

    const verb = item.versionActorAction === 'amended' ? 'Amended by' : 'Created by';

    return `${verb} ${item.versionActorName}`;
  }
}
